//@ts-nocheck
import { useMemo } from "react";

import { CCPOfferingContent } from "src/contentful";

import {
    CurrencyISOCode,
    DefaultPlanLimits,
    FormattedPriceCalculationResponse,
    OfferingHamsMap,
    OfferingSKUMap,
    PriceCalculationRequest,
    PricingPeriod,
    PricingPlans,
    ProductKey,
    ProductPlan,
    SpecialPlanNames
} from "../constants";

import { getFormattedPrice } from "./amounts-format";
import { extractPricingTiersFromEntry } from "./extract-pricing-tiers-from-entry";
import { calculatePrice } from "./price-calculation";

export const getFreePriceCalc = (
    currencyISOCode: CurrencyISOCode
): FormattedPriceCalculationResponse => {
    const zeroPrice = getFormattedPrice(currencyISOCode, 0);

    return {
        totalPrice: 0,
        avgPerUnitPrice: 0,

        formattedTotalPrice: zeroPrice,
        formattedAvgPerUnitPrice: zeroPrice,

        hasFlatRateFirstTier: true,

        breakdown: [
            {
                unitsInTier: 0,
                unitsAvailableInTier: 0,

                unitLimitLower: 0,
                unitLimitUpper: 0,

                totalPriceForTier: 0,
                avgPerUnitPriceForTier: 0
            }
        ],
        formattedBreakdown: [
            {
                formattedUnitsInTier: "0",
                formattedUnitsAvailableInTier: "0",

                formattedUnitLimitLower: "0",
                formattedUnitLimitUpper: "0",

                formattedTotalPriceForTier: zeroPrice,
                formattedAvgPerUnitPriceForTier: zeroPrice
            }
        ]
    };
};

export const usePriceFinderRequests = <P extends ProductKey>(
    pricingExampleUnitCounts: number[],
    productKey: string,
    productPlans: ProductPlan<P>[],
    unitMinEnterprise: number,
    unitCount: number,
    billingCycle: PricingPeriod
): PriceCalculationRequest[] => {
    return useMemo(() => {
        const getNumUnits = (planName: string, signupUnitLimit: number) => {
            let adjNumUnits = 0;

            // following scenarios do not have pricing and should send a zero unit count,
            // the zero pricing will not be displayed and is needed only to retain the
            // correct array order of the pricing requests/responses

            // Free plan has no pricing
            const isFree = planName === SpecialPlanNames.FREE;

            // Enterprise plans do not have monthly pricing
            const isMonthlyEnterprise =
                planName === SpecialPlanNames.ENTERPRISE &&
                billingCycle === "MONTHLY";

            // Enterprise plans have a minimum unit count
            const isUnmetUnitCountEnterprise =
                planName === SpecialPlanNames.ENTERPRISE &&
                unitCount < unitMinEnterprise;
            const isOverSignupUnitLimit = unitCount > signupUnitLimit;

            if (
                ![
                    isFree,
                    isMonthlyEnterprise,
                    isUnmetUnitCountEnterprise,
                    isOverSignupUnitLimit
                ].includes(true)
            ) {
                adjNumUnits = unitCount;
            }

            // keep unit count to general maximum limit
            return Math.min(adjNumUnits, DefaultPlanLimits.UNIT_LIMIT);
        };

        const planPriceReqs: PriceCalculationRequest[] = productPlans.map(
            ({ offeringSku, planName, signupLimit }) => ({
                ccpOfferingSKU: offeringSku,
                hamsProductKey:
                    productKey +
                    (planName === SpecialPlanNames.STANDARD
                        ? ""
                        : `.${planName.toLowerCase()}`),
                numUnits: getNumUnits(
                    planName,
                    signupLimit || DefaultPlanLimits.UNIT_LIMIT
                ),
                period: billingCycle
            })
        );

        const standardProductPlan = productPlans.find(
            (plan) => plan.planName === SpecialPlanNames.STANDARD
        );

        if (!standardProductPlan) {
            throw new Error(
                "Standard product plan was not provided as required for pricing example."
            );
        }

        const pricingExampleRequest: PriceCalculationRequest = {
            ccpOfferingSKU: standardProductPlan.offeringSku,
            hamsProductKey: productKey,
            numUnits:
                pricingExampleUnitCounts[billingCycle === "MONTHLY" ? 0 : 1] ||
                300,
            period: billingCycle
        };

        return planPriceReqs.concat(pricingExampleRequest);
    }, [
        productPlans,
        productKey,
        pricingExampleUnitCounts,
        billingCycle,
        unitCount,
        unitMinEnterprise
    ]);
};

export const usePricing = <P extends ProductKey>(
    ccpPricingData: OfferingSKUMap<P, CCPOfferingContent>,
    hamsPricingData: OfferingHamsMap<P, PricingPlans>,
    getPriceRequests: readonly PriceCalculationRequest[],
    currencyISOCode: CurrencyISOCode,
    localeISOCode: string
): FormattedPriceCalculationResponse[] => {
    const priceCalculations = [];

    for (const {
        ccpOfferingSKU,
        hamsProductKey,
        numUnits,
        period
    } of getPriceRequests) {
        if (numUnits === 0) {
            priceCalculations.push(getFreePriceCalc(currencyISOCode));
        } else {
            const pricingTiers = extractPricingTiersFromEntry(
                ccpPricingData,
                hamsPricingData,
                period,
                currencyISOCode,
                ccpOfferingSKU,
                hamsProductKey
            );

            if (!pricingTiers) {
                throw new Error(`Pricing tiers not found for ${period}.`);
            }

            priceCalculations.push(
                calculatePrice(
                    numUnits,
                    pricingTiers,
                    currencyISOCode,
                    localeISOCode
                )
            );
        }
    }

    return priceCalculations;
};
